// The local working copy folder (SPEC §4). Plain files plus `.hashquill/`.
// Hashquill only ever reads and writes `.md` files and `.hashquill/` (SPEC §12).
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { decodeText, encodeText, isTrackedPath, normalizePath } from './normalize';

export class FolderGoneError extends Error {
  constructor() { super('The specs folder was moved or deleted.'); this.name = 'FolderGoneError'; }
}
export class FolderPermissionError extends Error {
  constructor() { super('Hashquill no longer has permission to use the specs folder.'); this.name = 'FolderPermissionError'; }
}

const STATE_DIR = '.hashquill';
/** Pristine copy of the last pulled version of each file (base for line counts, splice and merges). */
export const BASE_DIR = `${STATE_DIR}/base`;
export const basePath = (path: string) => `${BASE_DIR}/${path}`;

export type FileStat = { path: string; size: number; lastModified: number };
/** OS clutter that doesn't make a folder "not empty". */
const IGNORABLE = new Set(['.DS_Store', 'desktop.ini', 'Thumbs.db', '.localized']);

type WritableLike = { write(d: Uint8Array<ArrayBuffer> | string): Promise<void>; close(): Promise<void>; abort(): Promise<void> };
type PermissionHandle = FileSystemDirectoryHandle & {
  queryPermission(d: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission(d: { mode: 'readwrite' }): Promise<PermissionState>;
  values(): AsyncIterable<FileSystemHandle>;
};

const allowedPath = (path: string) => isTrackedPath(path) || path.startsWith(`${STATE_DIR}/`);

function mapError(e: unknown): never {
  if (e instanceof DOMException) {
    if (e.name === 'NotFoundError') throw new FolderGoneError();
    if (e.name === 'NotAllowedError' || e.name === 'SecurityError') throw new FolderPermissionError();
  }
  throw e;
}

export class Folder {
  readonly handle: PermissionHandle;
  constructor(handle: FileSystemDirectoryHandle) { this.handle = handle as PermissionHandle; }

  get name(): string { return this.handle.name; }

  async permission(): Promise<PermissionState> {
    return this.handle.queryPermission({ mode: 'readwrite' });
  }
  /** Needs a user click (SPEC §4: Reconnect banner). */
  async requestPermission(): Promise<boolean> {
    return (await this.handle.requestPermission({ mode: 'readwrite' })) === 'granted';
  }

  /** Before every file operation: check the grant hasn't lapsed. */
  async #ensure(): Promise<void> {
    let p: PermissionState;
    try { p = await this.permission(); } catch (e) { mapError(e); }
    if (p !== 'granted') throw new FolderPermissionError();
  }

  async #dir(segments: string[], create: boolean): Promise<FileSystemDirectoryHandle> {
    let d: FileSystemDirectoryHandle = this.handle;
    for (const s of segments) d = await d.getDirectoryHandle(s, { create });
    return d;
  }

  async exists(path: string): Promise<boolean> {
    return (await this.readText(path)) !== null;
  }

  /** Normalized text, or null if the file doesn't exist. */
  async readText(path: string): Promise<string | null> {
    if (!allowedPath(path)) throw new Error(`Refusing to read ${path}`);
    await this.#ensure();
    const parts = path.split('/');
    try {
      const dir = await this.#dir(parts.slice(0, -1), false);
      const fh = await dir.getFileHandle(parts.at(-1)!);
      return decodeText(new Uint8Array(await (await fh.getFile()).arrayBuffer()));
    } catch (e) {
      if (e instanceof DOMException && (e.name === 'NotFoundError' || e.name === 'TypeMismatchError')) {
        await this.#assertRootAlive();
        return null;
      }
      mapError(e);
    }
  }

  /** Writes normalized text. createWritable() replaces the file only on close(); abort on error. */
  async writeText(path: string, text: string): Promise<void> {
    if (!allowedPath(path)) throw new Error(`Refusing to write ${path}`);
    await this.#ensure();
    const parts = path.split('/');
    let w: WritableLike | undefined;
    try {
      const dir = await this.#dir(parts.slice(0, -1), true);
      const fh = await dir.getFileHandle(parts.at(-1)!, { create: true });
      w = (await fh.createWritable()) as unknown as WritableLike;
      await w.write(encodeText(text));
      await w.close();
    } catch (e) {
      await w?.abort().catch(() => {});
      mapError(e);
    }
  }

  async remove(path: string): Promise<void> {
    const isBaseCopy = path.startsWith(`${BASE_DIR}/`) && path.toLowerCase().endsWith('.md');
    if (!isTrackedPath(path) && !isBaseCopy) throw new Error(`Refusing to delete ${path}`);
    await this.#ensure();
    const parts = path.split('/');
    try {
      const dir = await this.#dir(parts.slice(0, -1), false);
      await dir.removeEntry(parts.at(-1)!);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotFoundError') { await this.#assertRootAlive(); return; }
      mapError(e);
    }
  }

  /** All tracked (.md) files, NFC paths, sorted. Skips .hashquill/ and .git/. */
  async listTracked(): Promise<string[]> {
    return (await this.statTracked()).map((s) => s.path);
  }

  /** Tracked files with size and modification time, without reading their contents. */
  async statTracked(): Promise<FileStat[]> {
    await this.#ensure();
    const out: FileStat[] = [];
    const walk = async (dir: PermissionHandle, prefix: string) => {
      for await (const h of dir.values()) {
        const name = normalizePath(h.name);
        if (h.kind === 'directory') {
          if (name === STATE_DIR || name === '.git') continue;
          await walk(h as PermissionHandle, `${prefix}${name}/`);
        } else if (isTrackedPath(`${prefix}${name}`)) {
          const f = await (h as FileSystemFileHandle).getFile();
          out.push({ path: `${prefix}${name}`, size: f.size, lastModified: f.lastModified });
        }
      }
    };
    try { await walk(this.handle, ''); } catch (e) { mapError(e); }
    return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  }

  /** True if the folder has nothing in it except OS clutter. */
  async isEmpty(): Promise<boolean> {
    try {
      for await (const h of this.handle.values()) if (!IGNORABLE.has(h.name)) return false;
    } catch (e) { mapError(e); }
    return true;
  }

  async #assertRootAlive(): Promise<void> {
    try { for await (const _ of this.handle.values()) break; } catch (e) { mapError(e); }
  }
}

// ---------- Choosing and remembering the folder ----------

export type PickResult =
  | { kind: 'picked'; handle: FileSystemDirectoryHandle }
  | { kind: 'cancelled' }; // includes Chromium refusing a system folder (AbortError either way)

export async function pickFolder(): Promise<PickResult> {
  try {
    const handle = await (window as unknown as {
      showDirectoryPicker(o: { mode: 'readwrite'; startIn: 'documents'; id: string }): Promise<FileSystemDirectoryHandle>;
    }).showDirectoryPicker({ mode: 'readwrite', startIn: 'documents', id: 'hashquill-specs' });
    return { kind: 'picked', handle };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return { kind: 'cancelled' };
    throw e;
  }
}

const IDB_KEY = 'hashquill-folder';
/** IndexedDB holds the folder handle only (SPEC §2). */
export const rememberFolder = (h: FileSystemDirectoryHandle) => idbSet(IDB_KEY, h);
export const recallFolder = () => idbGet<FileSystemDirectoryHandle>(IDB_KEY);
export const forgetFolder = () => idbDel(IDB_KEY);
